import { ref, onMounted, onUnmounted, watch, computed, nextTick } from 'vue'
import axios from 'axios'

export function useCarComparisonBar(props, emit) {
  // Cache constants
  const CACHE_KEY = 'comparedCarsCache'
  const CACHE_EXPIRY = 30 * 60 * 1000 // 30 minutes in milliseconds

  // Safely check if we're in development mode
  const isDevelopment = ref(false)
  try {
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development') {
      isDevelopment.value = true
    }
  } catch (error) {
    // console.warn('Error checking environment mode:', error)
  }

  // Event handler references for proper cleanup
  const handleCarAddedToComparison = () => {
    // console.log('Detected car added to comparison from another component')
  }

  const handleCarRemovedFromComparison = () => {
    // console.log('Detected car removed from comparison from another component')
  }

  const comparedCars = ref([])
  const carouselContainer = ref(null)
  const scrollPosition = ref(0)
  const scrollStep = ref(200) // Pixels to scroll per click

  // Touch and mouse swipe support
  const touchStartX = ref(0)
  const touchEndX = ref(0)
  const mouseStartX = ref(0)
  const mouseEndX = ref(0)
  const isDragging = ref(false)
  const dragStartPosition = ref(0)
  const isMouseDown = ref(false)
  const justSwiped = ref(false)

  // Calculate maximum scroll position
  const maxScrollPosition = computed(() => {
    if (!carouselContainer.value) return 0

    const isMobile = window.innerWidth <= 768
    const itemWidth = isMobile ? 150 : 200 // Fixed width for mobile/desktop
    const totalItems = displayedCars.value.length + emptySlots.value
    const containerWidth = carouselContainer.value.clientWidth

    // Calculate total width of all items
    const totalWidth = totalItems * itemWidth

    // Calculate maximum scroll position
    // Add a small buffer to ensure the last item is fully visible
    const maxScroll = Math.max(0, totalWidth - containerWidth + (isMobile ? 70 : 120))

    return maxScroll
  })

  // Calculate total number of slides
  const totalSlides = computed(() => {
    if (!carouselContainer.value || displayedCars.value.length === 0) return 1

    const isMobile = window.innerWidth <= 768

    // Use fixed pixel widths to ensure consistent sizing
    const itemWidth = isMobile ? 150 : 200

    // Calculate items per slide - use whole numbers for mobile for better swipe experience
    const itemsPerSlide = isMobile ? 1 : 5
    const totalItems = displayedCars.value.length + emptySlots.value

    // Calculate total slides (round up to ensure all items are visible)
    const slides = Math.ceil(totalItems / itemsPerSlide)

    return slides
  })

  // Calculate current slide index (0-based)
  const currentSlideIndex = computed(() => {
    if (!carouselContainer.value || scrollPosition.value === 0) return 0

    const isMobile = window.innerWidth <= 768

    // Use fixed pixel widths to ensure consistent sizing
    const itemWidth = isMobile ? 150 : 200

    // Calculate items per slide - use whole numbers for mobile for better swipe experience
    const itemsPerSlide = isMobile ? 1 : 5

    // Calculate the width of a full slide (all items visible at once)
    const slideWidth = itemWidth * itemsPerSlide

    // Calculate current slide based on scroll position
    const index = Math.round(scrollPosition.value / slideWidth)

    return index
  })

  // Limit the number of displayed cars to maxComparedCars
  const displayedCars = computed(() => {
    return comparedCars.value.slice(0, props.maxComparedCars)
  })

  // Calculate the number of empty slots to show (to make a total of 5 slots, but only if we have fewer than 5 items)
  const emptySlots = computed(() => {
    const displayedCount = displayedCars.value.length
    const minItemsPerView = 5 // We want at least 5 items in the view

    // Only add empty slots if we have fewer than minItemsPerView items
    return displayedCount < minItemsPerView ? Math.max(0, minItemsPerView - displayedCount) : 0
  })

  // Cache management functions
  const getCachedCars = () => {
    try {
      const cacheData = localStorage.getItem(CACHE_KEY)
      if (!cacheData) return null

      const { cars, timestamp } = JSON.parse(cacheData)

      // Check if cache is expired
      if (Date.now() - timestamp > CACHE_EXPIRY) {
        localStorage.removeItem(CACHE_KEY)
        return null
      }

      return cars
    } catch (error) {
      // console.error('Error reading from cache:', error)
      return null
    }
  }

  const setCachedCars = (cars) => {
    try {
      const cacheData = {
        cars,
        timestamp: Date.now()
      }
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData))
    } catch (error) {
      // console.error('Error writing to cache:', error)
    }
  }

  // Fetch compared cars data
  const fetchComparedCars = async () => {
    try {
      if (props.comparisonList.length === 0) {
        comparedCars.value = []
        return
      }

      // Get cached cars
      const cachedCars = getCachedCars() || {}

      // Determine which cars need to be fetched
      const carsToFetch = props.comparisonList.filter(id => !cachedCars[id])

      // If we have all cars in cache, use them
      if (carsToFetch.length === 0) {
        comparedCars.value = props.comparisonList
          .map(id => cachedCars[id])
          .filter(car => car) // Filter out any undefined entries

        // Initialize carousel with cached data
        await initializeCarousel()
        return
      }

      // Process car offers in batches to prevent rate limiting
      const batchSize = 3; // Process 3 cars at a time
      const delay = 300; // 300ms delay between batches
      const newResults = [];

      // Split the cars to fetch into batches
      for (let i = 0; i < carsToFetch.length; i += batchSize) {
        const batch = carsToFetch.slice(i, i + batchSize);

        // Process this batch
        const batchPromises = batch.map(id =>
          axios.get(`/api/v1/car-offers/${id}`).then(response => response.data)
        );

        // Wait for this batch to complete
        const batchResults = await Promise.all(batchPromises);
        newResults.push(...batchResults);

        // If there are more batches to process, add a delay
        if (i + batchSize < carsToFetch.length) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      // Process new results and update cache
      const newCachedCars = { ...cachedCars }

      newResults.forEach(offer => {
        // Try to get the main_image_url from the car object
        let imageUrl = offer.car.main_image_url

        // If main_image_url is not available, try to get the first image from the images array
        if (!imageUrl && offer.car.images && offer.car.images.length > 0) {
          imageUrl = offer.car.images[0].box_path
        }

        const carData = {
          id: offer.id,
          name: offer.car.name,
          image_url: imageUrl
        }

        // Update cache
        newCachedCars[offer.id] = carData
      })

      // Save updated cache
      setCachedCars(newCachedCars)

      // Combine cached and new data
      comparedCars.value = props.comparisonList
        .map(id => newCachedCars[id])
        .filter(car => car) // Filter out any undefined entries

      // Initialize carousel with new data
      await initializeCarousel()
    } catch (error) {
      // console.error('Error fetching compared cars:', error)
    }
  }

  // Remove a car from comparison
  const removeFromComparison = (id) => {
    // Update local cache to reflect the removal
    try {
      const cachedCars = getCachedCars()
      if (cachedCars && cachedCars[id]) {
        // We don't actually remove it from cache, just from the display
        // This way if it's added back later, we still have the data
        // console.log('Car removed from comparison, keeping in cache:', id)
      }
    } catch (error) {
      // console.error('Error updating cache on car removal:', error)
    }

    emit('remove-from-comparison', id)
  }

  // Clear all compared cars
  const clearComparedCars = () => {
    // We don't clear the cache, just emit the event
    // This way if any cars are added back later, we still have their data
    // console.log('Clearing comparison list, keeping cache intact')

    emit('clear-comparison')
  }

  // Handle click on car image
  const handleCarClick = () => {
    // Only navigate if we didn't just finish swiping
    if (!justSwiped.value) {
      window.location.href = props.compareUrl
    } else {
      // console.log('Preventing navigation after swipe')
    }
  }

  // Update scroll step based on device type
  const updateScrollStep = () => {
    if (carouselContainer.value) {
      const isMobile = window.innerWidth <= 768

      // Use fixed pixel widths to ensure consistent sizing
      const itemWidth = isMobile ? 150 : 200

      // Calculate items per slide - use whole numbers for mobile for better swipe experience
      const itemsPerSlide = isMobile ? 1 : 5

      // Calculate the scroll step based on the number of items per slide
      scrollStep.value = Math.floor(itemWidth * itemsPerSlide)
    }
  }

  // Navigate to a specific slide
  const goToSlide = (slideIndex) => {
    try {
      // Validate input
      if (typeof slideIndex !== 'number' || slideIndex < 0) {
        return
      }

      // Check if carousel container exists
      if (!carouselContainer.value) {
        return
      }

      // Ensure transition is restored if it was removed during dragging
      const track = carouselContainer.value.querySelector('.carousel-track')
      if (track && track.style.transition === 'none') {
        track.style.transition = ''
      }

      // Ensure slideIndex is within bounds
      const maxSlideIndex = Math.max(0, totalSlides.value - 1)
      if (slideIndex > maxSlideIndex) {
        slideIndex = maxSlideIndex
      }

      const isMobile = window.innerWidth <= 768
      const containerWidth = carouselContainer.value.clientWidth

      // Calculate the width of a single item based on device type
      const itemWidth = isMobile ? 150 : 200

      // Calculate items per slide - use whole numbers for mobile for better swipe experience
      const itemsPerSlide = isMobile ? 1 : 5

      // Calculate the width of a full slide (all items visible at once)
      const slideWidth = itemsPerSlide * itemWidth

      // For partial slides (e.g., at the end), calculate how many items are in that slide
      const totalItems = displayedCars.value.length + emptySlots.value
      const itemsInLastSlide = totalItems % itemsPerSlide || itemsPerSlide

      // If this is the last slide and it's partial, adjust the slide width
      const isLastSlide = slideIndex === totalSlides.value - 1
      const hasMultipleSlides = totalSlides.value > 1
      const finalSlideWidth = isLastSlide && itemsInLastSlide < itemsPerSlide
        ? itemWidth * itemsInLastSlide
        : slideWidth

      // Calculate the target scroll position
      let targetPosition

      // If we have more items than can fit in the container, calculate proper slide positions
      if (maxScrollPosition.value > 0) {
        if (slideIndex === 0) {
          targetPosition = 0
        } else if (hasMultipleSlides && isLastSlide) {
          // For the last slide, ensure we scroll to show all remaining items completely
          targetPosition = maxScrollPosition.value
        } else {
          // For regular slides, calculate position based on slide index
          const normalizedSlideIndex = Math.min(slideIndex, Math.ceil(maxScrollPosition.value / slideWidth))
          targetPosition = normalizedSlideIndex * slideWidth
        }
      } else {
        // If all items fit in the container, no need to scroll
        targetPosition = 0
      }

      // Ensure the target position is within bounds
      const oldPosition = scrollPosition.value
      const newPosition = Math.min(maxScrollPosition.value, Math.max(0, targetPosition))

      // Only update if the position actually changed
      if (oldPosition !== newPosition) {
        // Update the scroll position
        scrollPosition.value = newPosition

        // Force a reflow to ensure the DOM is updated
        carouselContainer.value.offsetHeight

        // Force another reflow after a short delay to ensure the transition is applied
        setTimeout(() => {
          if (carouselContainer.value) {
            carouselContainer.value.offsetHeight
          }
        }, 10)
      }
    } catch (error) {
      // console.error('Error in goToSlide:', error)
    }
  }

  // Scroll functions
  const scrollLeft = (event) => {
    // Go to previous slide
    if (currentSlideIndex.value > 0) {
      goToSlide(currentSlideIndex.value - 1)
    }
  }

  const scrollRight = (event) => {
    // Go to next slide
    if (currentSlideIndex.value < totalSlides.value - 1) {
      goToSlide(currentSlideIndex.value + 1)
    }
  }

  // Touch event handlers
  const handleTouchStart = (event) => {
    try {
      // Don't initiate drag if touching the remove button
      if (event.target.closest('.comparation-compact__remove')) {
        return
      }

      // Store the initial touch position
      if (event.touches && event.touches.length > 0) {
        touchStartX.value = event.touches[0].clientX

        // Set dragging state and store initial scroll position
        isDragging.value = true
        dragStartPosition.value = scrollPosition.value

        // Remove transition during dragging for immediate response
        if (carouselContainer.value) {
          const track = carouselContainer.value.querySelector('.carousel-track')
          if (track) {
            track.style.transition = 'none'
          }
        }

        // Explicitly prevent default to ensure no scrolling occurs
        event.preventDefault()
      }
    } catch (error) {
      // console.error('Error in handleTouchStart:', error)
    }
  }

  const handleTouchMove = (event) => {
    try {
      // Only process if we're dragging
      if (!isDragging.value) return

      // Update the current touch position
      if (event.touches && event.touches.length > 0) {
        touchEndX.value = event.touches[0].clientX

        // Calculate the current swipe distance
        const swipeDistance = touchEndX.value - touchStartX.value

        // Calculate new position based on drag distance with a damping factor for smoother feel
        let newPosition = dragStartPosition.value - (swipeDistance * 0.8)

        // Add resistance when dragging beyond bounds
        if (newPosition < 0) {
          // Dragging beyond the start - add resistance
          newPosition = newPosition * 0.3
        } else if (newPosition > maxScrollPosition.value) {
          // Dragging beyond the end - add resistance
          const overDrag = newPosition - maxScrollPosition.value
          newPosition = maxScrollPosition.value + overDrag * 0.3
        }

        // Update scroll position in real-time
        scrollPosition.value = newPosition

        // Explicitly prevent default to ensure no scrolling occurs
        event.preventDefault()
      }
    } catch (error) {
      // console.error('Error in handleTouchMove:', error)
    }
  }

  const handleTouchEnd = (event) => {
    try {
      // Only process if we're dragging
      if (!isDragging.value) return

      // Calculate swipe distance
      const swipeDistance = touchEndX.value - touchStartX.value

      // Minimum swipe distance to trigger navigation (in pixels)
      const isMobile = window.innerWidth <= 768
      const minSwipeDistance = isMobile ? 15 : 30

      // Restore transition for smooth snapping
      if (carouselContainer.value) {
        const track = carouselContainer.value.querySelector('.carousel-track')
        if (track) {
          track.style.transition = '' // Restore default transition
        }
      }

      // Determine which slide to snap to based on swipe distance and velocity
      if (touchStartX.value !== 0 && touchEndX.value !== 0) {
        const itemWidth = isMobile ? 150 : 200
        const itemsPerSlide = isMobile ? 1 : 5
        const slideWidth = itemWidth * itemsPerSlide

        if (swipeDistance > minSwipeDistance) {
          // Swipe right (finger moves right) - show previous slide
          if (currentSlideIndex.value > 0) {
            goToSlide(currentSlideIndex.value - 1)
          } else {
            // Snap back to first slide
            goToSlide(0)
          }
        } else if (swipeDistance < -minSwipeDistance) {
          // Swipe left (finger moves left) - show next slide
          if (currentSlideIndex.value < totalSlides.value - 1) {
            goToSlide(currentSlideIndex.value + 1)
          } else {
            // Snap back to last slide
            goToSlide(totalSlides.value - 1)
          }
        } else {
          // Swipe distance too small, snap to nearest slide
          const nearestSlideIndex = Math.round(scrollPosition.value / slideWidth)
          const clampedSlideIndex = Math.max(0, Math.min(nearestSlideIndex, totalSlides.value - 1))
          goToSlide(clampedSlideIndex)
        }
      } else {
        // If we don't have valid touch coordinates, snap back to current slide
        goToSlide(currentSlideIndex.value)
      }

      // Reset dragging state
      isDragging.value = false

      // Set justSwiped flag to true if there was actual movement
      if (Math.abs(swipeDistance) > 5) {
        justSwiped.value = true
        // Reset the flag after a short delay
        setTimeout(() => {
          justSwiped.value = false
        }, 300)
      }

      // Reset touch positions
      touchStartX.value = 0
      touchEndX.value = 0
    } catch (error) {
      // console.error('Error in handleTouchEnd:', error)
    }
  }

  // Mouse event handlers
  const handleMouseDown = (event) => {
    try {
      // Don't initiate drag if clicking on the remove button
      if (event.target.closest('.comparation-compact__remove')) {
        return
      }

      // Store the initial mouse position
      mouseStartX.value = event.clientX

      // Set dragging state and store initial scroll position
      isDragging.value = true
      isMouseDown.value = true
      dragStartPosition.value = scrollPosition.value

      // Remove transition during dragging for immediate response
      if (carouselContainer.value) {
        const track = carouselContainer.value.querySelector('.carousel-track')
        if (track) {
          track.style.transition = 'none'
        }
      }

      // Prevent default to avoid text selection during drag
      event.preventDefault()
    } catch (error) {
      // console.error('Error in handleMouseDown:', error)
    }
  }

  const handleMouseMove = (event) => {
    try {
      // Only process if mouse is down
      if (!isMouseDown.value) return

      // Update the current mouse position
      mouseEndX.value = event.clientX

      // Calculate the current swipe distance
      const swipeDistance = mouseEndX.value - mouseStartX.value

      // Calculate new position based on drag distance with a damping factor for smoother feel
      let newPosition = dragStartPosition.value - (swipeDistance * 0.8)

      // Add resistance when dragging beyond bounds
      if (newPosition < 0) {
        // Dragging beyond the start - add resistance
        newPosition = newPosition * 0.3
      } else if (newPosition > maxScrollPosition.value) {
        // Dragging beyond the end - add resistance
        const overDrag = newPosition - maxScrollPosition.value
        newPosition = maxScrollPosition.value + overDrag * 0.3
      }

      // Update scroll position in real-time
      scrollPosition.value = newPosition

      // Prevent default to avoid text selection during drag
      event.preventDefault()
    } catch (error) {
      // console.error('Error in handleMouseMove:', error)
    }
  }

  const handleMouseUp = (event) => {
    try {
      // Only process if mouse is down
      if (!isMouseDown.value) return

      // Calculate swipe distance
      const swipeDistance = mouseEndX.value - mouseStartX.value

      // Minimum swipe distance to trigger navigation (in pixels)
      const isMobile = window.innerWidth <= 768
      const minSwipeDistance = isMobile ? 15 : 30

      // Restore transition for smooth snapping
      if (carouselContainer.value) {
        const track = carouselContainer.value.querySelector('.carousel-track')
        if (track) {
          track.style.transition = '' // Restore default transition
        }
      }

      // Determine which slide to snap to based on swipe distance
      if (mouseStartX.value !== 0 && mouseEndX.value !== 0) {
        const itemWidth = isMobile ? 150 : 200
        const itemsPerSlide = isMobile ? 1 : 5
        const slideWidth = itemWidth * itemsPerSlide

        if (swipeDistance > minSwipeDistance) {
          // Swipe right - show previous slide
          if (currentSlideIndex.value > 0) {
            goToSlide(currentSlideIndex.value - 1)
          } else {
            // Snap back to first slide
            goToSlide(0)
          }
        } else if (swipeDistance < -minSwipeDistance) {
          // Swipe left - show next slide
          if (currentSlideIndex.value < totalSlides.value - 1) {
            goToSlide(currentSlideIndex.value + 1)
          } else {
            // Snap back to last slide
            goToSlide(totalSlides.value - 1)
          }
        } else {
          // Swipe distance too small, snap to nearest slide
          const nearestSlideIndex = Math.round(scrollPosition.value / slideWidth)
          const clampedSlideIndex = Math.max(0, Math.min(nearestSlideIndex, totalSlides.value - 1))
          goToSlide(clampedSlideIndex)
        }
      } else {
        // If we don't have valid mouse coordinates, snap back to current slide
        goToSlide(currentSlideIndex.value)
      }

      // Reset dragging state
      isDragging.value = false
      isMouseDown.value = false

      // Set justSwiped flag to true if there was actual movement
      if (Math.abs(swipeDistance) > 5) {
        justSwiped.value = true
        // Reset the flag after a short delay
        setTimeout(() => {
          justSwiped.value = false
        }, 300)
      }

      // Reset mouse positions
      mouseStartX.value = 0
      mouseEndX.value = 0
    } catch (error) {
      // console.error('Error in handleMouseUp:', error)
    }
  }

  // Handle document-level mouseup to catch events outside the carousel
  const handleDocumentMouseUp = (event) => {
    if (isMouseDown.value) {
      handleMouseUp(event)
    }
  }

  // Initialize carousel and handle window resize
  const initializeCarousel = async () => {
    // Reset scroll position
    scrollPosition.value = 0

    // Wait for the DOM to update
    await nextTick()

    // Check if carousel container exists
    if (!carouselContainer.value) {
      return
    }

    // Update scroll step
    updateScrollStep()

    // Force a reflow to ensure the DOM is updated
    carouselContainer.value.offsetHeight
  }

  // Handle window resize
  const handleResize = async () => {
    // Wait for the DOM to update with new dimensions
    await nextTick()

    // Check if carousel container exists
    if (!carouselContainer.value) {
      return
    }

    // Update scroll step based on new dimensions
    updateScrollStep()

    // Force a reflow to ensure the DOM is updated
    carouselContainer.value.offsetHeight

    // Ensure we're still on a valid slide after resize
    if (currentSlideIndex.value >= totalSlides.value) {
      goToSlide(totalSlides.value - 1)
    } else {
      // Recalculate position for current slide with new dimensions
      goToSlide(currentSlideIndex.value)
    }
  }

  // Handle keyboard navigation
  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      scrollLeft()
    } else if (event.key === 'ArrowRight') {
      scrollRight()
    }
  }

  // Handle pagination dot click
  const handlePaginationClick = (slideIndex) => {
    // Navigate to the selected slide
    goToSlide(slideIndex)
  }

  // Force a reflow of the DOM
  const forceReflow = () => {
    if (carouselContainer.value) {
      // Access offsetHeight to force a reflow
      const height = carouselContainer.value.offsetHeight

      // Force update of the transform
      const currentTransform = scrollPosition.value
      scrollPosition.value = currentTransform + 0.1

      // Force another reflow
      carouselContainer.value.offsetHeight

      // Set back to the original position
      setTimeout(() => {
        scrollPosition.value = currentTransform
      }, 50)
    }
  }

  // Watch for changes in the comparison list
  watch(() => props.comparisonList, (newList, oldList) => {
    // Fetch new data when the list changes
    fetchComparedCars()
  }, { deep: true })

  // Watch for changes in displayedCars to update carousel
  watch(() => displayedCars.value.length, async (newLength, oldLength) => {
    // Wait for the DOM to update
    await nextTick()

    // Re-initialize the carousel when the number of displayed cars changes
    await initializeCarousel()
  })

  // Initialize on mount
  const initializeComparisonBar = async () => {
    await fetchComparedCars()

    // Add window resize listener
    window.addEventListener('resize', handleResize)

    // Add keyboard navigation
    window.addEventListener('keydown', handleKeyDown)

    // Add document-level mouseup event listener
    document.addEventListener('mouseup', handleDocumentMouseUp)

    // Add event listeners for comparison list changes from other components
    window.addEventListener('carAddedToComparison', handleCarAddedToComparison)
    window.addEventListener('carRemovedFromComparison', handleCarRemovedFromComparison)

    // Initialize carousel
    await initializeCarousel()

    // Create a ResizeObserver to detect changes to the container size
    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(async () => {
        await handleResize()
      })

      if (carouselContainer.value) {
        resizeObserver.observe(carouselContainer.value)
      }
    }
  }

  // Cleanup function
  const cleanupComparisonBar = () => {
    window.removeEventListener('resize', handleResize)
    window.removeEventListener('keydown', handleKeyDown)
    document.removeEventListener('mouseup', handleDocumentMouseUp)

    // Remove comparison list change event listeners
    window.removeEventListener('carAddedToComparison', handleCarAddedToComparison)
    window.removeEventListener('carRemovedFromComparison', handleCarRemovedFromComparison)
  }

  return {
    // Refs
    isDevelopment,
    comparedCars,
    carouselContainer,
    scrollPosition,
    scrollStep,
    touchStartX,
    touchEndX,
    mouseStartX,
    mouseEndX,
    isDragging,
    dragStartPosition,
    isMouseDown,
    justSwiped,

    // Computed
    maxScrollPosition,
    totalSlides,
    currentSlideIndex,
    displayedCars,
    emptySlots,

    // Methods
    fetchComparedCars,
    removeFromComparison,
    clearComparedCars,
    handleCarClick,
    updateScrollStep,
    goToSlide,
    scrollLeft,
    scrollRight,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleDocumentMouseUp,
    initializeCarousel,
    handleResize,
    handleKeyDown,
    handlePaginationClick,
    forceReflow,
    initializeComparisonBar,
    cleanupComparisonBar
  }
}
